import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, AuthUser } from './api';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('nslive_user');
    const token = localStorage.getItem('nslive_token');
    if (stored && token) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        localStorage.removeItem('nslive_user');
        localStorage.removeItem('nslive_token');
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await apiLogin(username, password);
    const { access_token, role, streamer_id, permission } = res.data;
    localStorage.setItem('nslive_token', access_token);
    const authUser: AuthUser = {
      sub: username,
      role: role as 'admin' | 'streamer',
      user_id: 0,
      streamer_id,
      permission: permission as 'read' | 'write' | null,
    };
    localStorage.setItem('nslive_user', JSON.stringify(authUser));
    setUser(authUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('nslive_token');
    localStorage.removeItem('nslive_user');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
